import { TFunction } from 'i18next';
import { toNumber } from 'lodash';
import { UPDropdownItem } from 'components/dropdown';
import {
  COMPANY_PAYROLL__DUPLICATE_PER_JOINT_COMMISSION,
  COMPANY_PAYROLL__EMPLOYEE,
  COMPANY_PAYROLL__EMPLOYEE_REQUIRED,
  COMPANY_PAYROLL__FUNCTION,
  COMPANY_PAYROLL__FUNCTION_REQUIRED,
  COMPANY_PAYROLL__JOINT_COMMISSION_REQUIRED,
  COMPANY_PAYROLL__WAGE_EXEPTION_VALUE_NOT_ZERO,
  COMPANY_PAYROLL__WORKERCLASS,
  COMPANY_PAYROLL__WORKERCLASS_REQUIRED,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import IWageExemptionsUpdateError, {
  IWageExemptionOffendingRow,
  WageExemptionKeyType,
} from 'types/company/payroll/IWageExemptionsUpdateError';
import { WageExemptionField, WageExemptionGridErrors } from '../types';

export const arrayToString = (data: { keyId: string; jointCommissionId: string }[]): string => {
  return data.map(obj => `${obj.keyId} - ${obj.jointCommissionId}`).join(', ');
};

export interface WageExemptionLabelSources {
  employees: UPDropdownItem[];
  functions: UPDropdownItem[];
  workerclasses: UPDropdownItem[];
  paritairComites: UPDropdownItem[];
}

/**
 * The local row ids of each grid, in the order they were submitted. The API points at rejected rows by
 * position, since rows the user just added all travel with an id of 0.
 */
export type WageExemptionRowIds = Record<WageExemptionKeyType, (string | number)[]>;

export const EMPTY_WAGE_EXEMPTION_ROW_IDS: WageExemptionRowIds = { workerclass: [], function: [], employee: [] };

export type WageExemptionGridErrorsByGrid = Record<WageExemptionKeyType, WageExemptionGridErrors>;

// Reported in the order the grids appear on the page.
const KEY_TYPES: WageExemptionKeyType[] = ['workerclass', 'function', 'employee'];

const NO_FIELDS: WageExemptionField[] = [];

// Each rule points at one editable column, so the rejection can be narrowed from the row down to the cell
// the user actually has to fix. A duplicate is a (key, joint commission) pair, so both halves are flagged.
const FIELDS_BY_ERROR_KEY: Record<string, WageExemptionField[]> = {
  [COMPANY_PAYROLL__WORKERCLASS_REQUIRED]: ['key'],
  [COMPANY_PAYROLL__FUNCTION_REQUIRED]: ['key'],
  [COMPANY_PAYROLL__EMPLOYEE_REQUIRED]: ['key'],
  [COMPANY_PAYROLL__JOINT_COMMISSION_REQUIRED]: ['jointCommission'],
  [COMPANY_PAYROLL__WAGE_EXEPTION_VALUE_NOT_ZERO]: ['wage'],
  [COMPANY_PAYROLL__DUPLICATE_PER_JOINT_COMMISSION]: ['key', 'jointCommission'],
};

export const EMPTY_WAGE_EXEMPTION_GRID_ERRORS: WageExemptionGridErrorsByGrid = {
  workerclass: { rowIds: [], fields: NO_FIELDS },
  function: { rowIds: [], fields: NO_FIELDS },
  employee: { rowIds: [], fields: NO_FIELDS },
};

const GRID_LABEL_KEY: Record<WageExemptionKeyType, string> = {
  workerclass: COMPANY_PAYROLL__WORKERCLASS,
  function: COMPANY_PAYROLL__FUNCTION,
  employee: COMPANY_PAYROLL__EMPLOYEE,
};

const findLabel = (items: UPDropdownItem[], value: string): string | undefined =>
  items?.find(item => item.value?.toString() === value)?.label;

const rowLabel = (row: IWageExemptionOffendingRow, labels: WageExemptionLabelSources): string => {
  switch (row.keyType) {
    case 'workerclass':
      return findLabel(labels.workerclasses, row.keyValue) ?? row.keyValue;
    case 'function':
      return findLabel(labels.functions, row.keyValue) ?? row.keyValue;
    default:
      return labels.employees?.find(item => toNumber(item.value) === toNumber(row.keyValue))?.label ?? row.keyValue;
  }
};

const jointCommissionLabel = (paritairComiteId: number, labels: WageExemptionLabelSources): string =>
  findLabel(labels.paritairComites, paritairComiteId?.toString()) ?? paritairComiteId?.toString();

/**
 * Maps the rejected rows back onto the ids the grids render with, and works out which cell of those rows
 * to flag. Resolving positions to ids once, while the submitted order still holds, keeps the highlight on
 * the right row even if the user then adds or deletes rows above it.
 */
export const resolveWageExemptionGridErrors = (
  error: IWageExemptionsUpdateError,
  submittedRowIds: WageExemptionRowIds,
): WageExemptionGridErrorsByGrid => {
  if (!error) return EMPTY_WAGE_EXEMPTION_GRID_ERRORS;

  const fields = FIELDS_BY_ERROR_KEY[error.key] ?? NO_FIELDS;
  const resolved: WageExemptionGridErrorsByGrid = {
    workerclass: { rowIds: [], fields },
    function: { rowIds: [], fields },
    employee: { rowIds: [], fields },
  };

  error.rows.forEach(row => {
    const id = submittedRowIds[row.keyType]?.[row.index];
    if (id !== undefined) resolved[row.keyType].rowIds.push(id);
  });

  return resolved;
};

/**
 * Turns the translation key the API rejected a save with into the message shown above the grids. Only the
 * duplicate failure needs more than the key itself: it names the offending rows using the labels already
 * loaded in the dropdowns.
 */
export const buildWageExemptionsUpdateErrorMessage = (
  error: IWageExemptionsUpdateError,
  t: TFunction,
  labels: WageExemptionLabelSources,
): string => {
  if (!error) return '';
  if (!error.key) return t(GENERAL__ERROR_MESSAGE);
  if (error.key !== COMPANY_PAYROLL__DUPLICATE_PER_JOINT_COMMISSION || error.rows.length === 0) {
    return t(error.key);
  }

  const perGrid = KEY_TYPES.map(keyType => {
    const rows = error.rows.filter(row => row.keyType === keyType);
    if (rows.length === 0) return '';

    // Both sides of a collision are reported so they can both be highlighted, but naming the pair twice
    // would only make the message harder to read.
    const seen = new Set<string>();
    const pairs = rows
      .filter(row => {
        const pair = `${row.keyValue}|${row.paritairComiteId}`;
        if (seen.has(pair)) return false;
        seen.add(pair);
        return true;
      })
      .map(row => ({
        keyId: rowLabel(row, labels),
        jointCommissionId: jointCommissionLabel(row.paritairComiteId, labels),
      }));

    return `${t(GRID_LABEL_KEY[keyType])}: ${arrayToString(pairs)}`;
  }).filter(segment => segment !== '');

  return `${t(COMPANY_PAYROLL__DUPLICATE_PER_JOINT_COMMISSION)} ${perGrid.join('; ')}`;
};

/**
 * Whether a row is one the user just added and has not saved yet.
 *
 * New rows are given an id from `Math.random()`, so they sit below 1 while every id the API hands back is
 * a positive integer — the same test the save uses to decide which rows travel with an id of 0.
 *
 * Grids exempt these rows from the search filter: a row that has only just been added is still blank, so
 * it matches no search term and would vanish from the grid the moment it was created.
 */
export const isUnsavedWageExemptionRow = (row: { id: number }): boolean => row.id < 1;
